import React, { useState, useMemo } from "react";
import {
  format,
  isAfter,
  isBefore,
  addDays,
  differenceInDays,
  parseISO,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  eachWeekOfInterval,
  eachMonthOfInterval,
} from "date-fns";
import { MoreHorizontal } from "lucide-react";
import {
  Paper,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  IconButton,
} from "@mui/material";
import { indigo, red, green, grey } from "@mui/material/colors";
import { getStatusColor } from "../utils";
import { useTheme } from "@emotion/react";

const GanttChart = ({ tasks, users, project, onTaskClick, onRenameColumn }) => {
  const [viewMode, setViewMode] = useState("Day");
  const theme = useTheme();

  const statusColumns = project?.columns || [
    "Backlog",
    "To Do",
    "In Progress",
    "Review",
    "Done",
  ];

  const groupedTasks = useMemo(() => {
    return statusColumns.map((status) => ({
      status,
      tasks: [...tasks]
        .filter((t) => t.status === status)
        .sort(
          (a, b) =>
            new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
        ),
    }));
  }, [tasks, statusColumns]);

  const sortedTasks = useMemo(
    () =>
      [...tasks].sort(
        (a, b) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      ),
    [tasks]
  );

  // Calculate timeline boundaries
  const minDate = useMemo(() => {
    if (sortedTasks.length === 0) return startOfWeek(new Date());
    const earliest = parseISO(sortedTasks[0].startDate);
    return viewMode === "Month"
      ? startOfMonth(earliest)
      : startOfWeek(earliest);
  }, [sortedTasks, viewMode]);

  const maxDate = useMemo(() => {
    if (sortedTasks.length === 0) return endOfWeek(new Date());
    const latest = parseISO(
      sortedTasks.reduce((max, t) =>
        isAfter(parseISO(t.dueDate), parseISO(max.dueDate)) ? t : max
      ).dueDate
    );
    return viewMode === "Month"
      ? endOfMonth(addDays(latest, 30))
      : addDays(latest, viewMode === "Day" ? 7 : 21);
  }, [sortedTasks, viewMode]);

  // View constants
  const PX_PER_DAY = viewMode === "Day" ? 50 : viewMode === "Week" ? 15 : 4;

  // Generate Timeline Columns
  const columns = useMemo(() => {
    if (viewMode === "Day") {
      return eachDayOfInterval({ start: minDate, end: maxDate }).map((d) => ({
        date: d,
        label: format(d, "d"),
        subLabel: format(d, "EEE"),
        width: PX_PER_DAY,
      }));
    } else if (viewMode === "Week") {
      return eachWeekOfInterval({ start: minDate, end: maxDate }).map((d) => ({
        date: d,
        label: `Week ${format(d, "w")}`,
        subLabel: format(d, "MMM d"),
        width: PX_PER_DAY * 7,
      }));
    } else {
      return eachMonthOfInterval({ start: minDate, end: maxDate }).map((d) => {
        const daysInMonth =
          differenceInDays(endOfMonth(d), startOfMonth(d)) + 1;
        return {
          date: d,
          label: format(d, "MMMM"),
          subLabel: format(d, "yyyy"),
          width: PX_PER_DAY * daysInMonth,
        };
      });
    }
  }, [minDate, maxDate, viewMode, PX_PER_DAY]);

  if (sortedTasks.length === 0)
    return (
      <Box p={4} textAlign="center" color="text.secondary">
        No tasks to display in Gantt
      </Box>
    );

  const getBarColor = (task, isOverdue) => {
    if (isOverdue) return { bgcolor: red[500], borderColor: red[700] };
    if (task.status === "Done")
      return { bgcolor: green[500], borderColor: green[700] };
    return { bgcolor: indigo[500], borderColor: indigo[700] };
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: 600,
      }}
    >
      {/* Toolbar */}
      <Box
        p={2}
        borderBottom="1px solid"
        borderColor="divider"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h6" fontWeight="bold" color="text.secondary">
          Timeline
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => {
            if (newMode) setViewMode(newMode);
          }}
          size="small"
        >
          {["Day", "Week", "Month"].map((mode) => (
            <ToggleButton key={mode} value={mode}>
              {mode}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", position: "relative" }}>
        <Box
          sx={{
            minWidth: "max-content",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              bgcolor: "background.paper",
              position: "sticky",
              top: 0,
              zIndex: 100,
              borderBottom: "1px solid",
              borderColor: "divider",
              height: 56,
            }}
          >
            <Box
              sx={{
                width: 256,
                flexShrink: 0,
                p: 2,
                borderRight: "1px solid",
                borderColor: "divider",
                fontWeight: "bold",
                fontSize: "0.875rem",
                position: "sticky",
                left: 0,
                zIndex: 110,
                bgcolor: "background.paper",
                display: "flex",
                alignItems: "center",
                boxShadow: "4px 0 10px -4px rgba(0,0,0,0.1)",
              }}
            >
              Task Name
            </Box>
            <Box display="flex" height="100%">
              {columns.map((col, i) => (
                <Box
                  key={i}
                  sx={{
                    flexShrink: 0,
                    borderRight: "1px solid",
                    borderColor: "divider",
                    p: 1,
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    width: col.width,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    noWrap
                    display="block"
                  >
                    {col.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", fontSize: "0.6rem" }}
                  >
                    {col.subLabel}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Grid Background Layer */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              top: 56,
              left: 256,
              pointerEvents: "none",
              display: "flex",
              zIndex: 0,
            }}
          >
            {columns.map((col, i) => (
              <Box
                key={i}
                sx={{
                  flexShrink: 0,
                  borderRight: "1px solid",
                  borderRadius: 2,
                  borderColor:
                    theme.palette.mode === "dark" ? grey[700] : grey[100], // Faint line
                  height: "100%",
                  width: col.width,
                }}
              />
            ))}
          </Box>

          {/* Rows */}
          <Box sx={{ position: "relative", zIndex: 10 }}>
            {groupedTasks.map((group) => (
              <Box key={group.status}>
                {/* Status Header Row */}
                <Box
                  sx={{
                    display: "flex",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? grey[900] : grey[50],
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    height: 40,
                    position: "sticky",
                    top: 56,
                    zIndex: 30,
                  }}
                >
                  <Box
                    sx={{
                      width: 256,
                      flexShrink: 0,
                      px: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontWeight: "bold",
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      textTransform: "uppercase",
                      borderRight: "1px solid",
                      borderColor: "divider",
                      position: "sticky",
                      left: 0,
                      bgcolor: "inherit",
                      zIndex: 40,
                    }}
                  >
                    {group.status}
                    <IconButton
                      size="small"
                      onClick={() => onRenameColumn(group.status)}
                      sx={{ ml: 1 }}
                    >
                      <MoreHorizontal size={14} />
                    </IconButton>
                  </Box>
                  <Box sx={{ flex: 1 }} />
                </Box>

                {/* Tasks in this Status */}
                {group.tasks.map((task) => {
                  const startOffset = differenceInDays(
                    parseISO(task.startDate),
                    minDate
                  );
                  const duration =
                    differenceInDays(
                      parseISO(task.dueDate),
                      parseISO(task.startDate)
                    ) + 1;
                  const isOverdue =
                    isBefore(parseISO(task.dueDate), new Date()) &&
                    task.status !== "Done";

                  const leftPos = startOffset * PX_PER_DAY;
                  const width = Math.max(duration * PX_PER_DAY, PX_PER_DAY); // Minimum 1 unit width
                  const barColors = getBarColor(task, isOverdue);

                  return (
                    <Box
                      key={task.id}
                      sx={{
                        display: "flex",
                        borderBottom: "1px solid",
                        borderRadius: 2,
                        borderColor:
                          theme.palette.mode === "dark" ? grey[700] : grey[100],
                        height: 48,
                        position: "relative",
                        "&:hover": {
                          "& .sticky-col": {
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? grey[700]
                                : grey[50],
                          },
                        },
                      }}
                    >
                      {/* Sticky Task Name */}
                      <Box
                        className="sticky-col"
                        onClick={() => onTaskClick(task)}
                        sx={{
                          width: 256,
                          flexShrink: 0,
                          p: 2,
                          borderRight: "1px solid",
                          borderColor: "divider",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          color: "text.primary",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          position: "sticky",
                          left: 0,
                          zIndex: 20,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          cursor: "pointer",
                          boxShadow: "4px 0 10px -4px rgba(0,0,0,0.1)",
                          transition: "background-color 0s", // Instant switch
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor:
                              task.status === "Done" ? green[500] : indigo[500],
                          }}
                        />
                        {task.title}
                      </Box>

                      {/* Task Bar Area */}
                      <Box sx={{ position: "relative", flex: 1 }}>
                        <Tooltip
                          title={`${task.title} (${format(
                            parseISO(task.startDate),
                            "MMM d"
                          )} - ${format(parseISO(task.dueDate), "MMM d")})`}
                        >
                          <Box
                            onClick={() => onTaskClick(task)}
                            sx={{
                              position: "absolute",
                              top: 10,
                              height: 28,
                              borderRadius: 1,
                              boxShadow: 1,
                              border: "1px solid",
                              display: "flex",
                              alignItems: "center",
                              px: 1,
                              color: "white",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              "&:hover": {
                                boxShadow: 3,
                                zIndex: 5,
                              },
                              bgcolor: barColors.bgcolor,
                              borderColor: barColors.borderColor,
                              left: leftPos,
                              width: width,
                            }}
                          >
                            {width > 30 && (
                              <Typography
                                variant="caption"
                                noWrap
                                fontWeight="bold"
                              >
                                {task.title}
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default GanttChart;
